import { observer } from 'mobx-react-lite'
import { forwardRef, useContext, useEffect } from 'react'
import { useReactNodeView } from '../ReactNodeView/ReactNodeViewContext'
import { ReactComponent as ArrowUpIcon } from './icons/ArrowUp.svg'
import styles from './VoteListItem.module.scss'
import cx from 'classnames'
import { RootState } from 'types'
import { useSelector } from 'react-redux'
import { isEqual, last } from 'lodash'
import { Node as ProseNode } from 'prosemirror-model'
import { schema } from 'prose-core'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useUserNames } from './useUserNames'
import { ViewEditableContext } from 'screens/ProseEdit/components/ViewEditableContextProvider/ViewEditableContextProvider'

export const VoteListItem = observer(
  forwardRef<HTMLDivElement, any>(function VoteListItem({ children }, contentDomRef) {
    const { nodeView, view, getPos, inCommentMode } = useReactNodeView()
    const { editable } = useContext(ViewEditableContext) ?? { editable: process.env.NODE_ENV === 'test' ? true : false }

    const isVotable = editable && !inCommentMode
    const { auth /*, anonymousUser*/ } = useSelector(
      (state: RootState) => ({
        auth: state.auth,
        anonymousUser: state.anonymousUser,
        unregisteredUser: state.unregisteredUser,
      }),
      isEqual,
    )
    useEffect(() => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      nodeView.dom!.dataset.depth = nodeView.node.attrs.depth
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      const originalUpdateFn = nodeView.update
      nodeView.update = (node: ProseNode) => {
        if (node.type === schema.nodes.vote_list_item) {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          nodeView.dom!.dataset.depth = node.attrs.depth
          return (originalUpdateFn as any).call(nodeView, node)
        }
        return (originalUpdateFn as any).call(nodeView, node)
      }
    }, [nodeView])

    const voters = new Set<number>(view.state.tr.doc.resolve(getPos()).nodeAfter?.attrs.voters ?? [])
    const userNames = useUserNames(voters)
    const votedByMe = voters.has(auth?.id ?? NaN)
    const handleVote = () => {
      // eslint-disable-next-line eqeqeq
      if (!isVotable || auth?.id == undefined) return
      const {
        state: { tr },
        dispatch,
      } = view
      const oldAttrs = tr.doc.resolve(getPos()).nodeAfter?.attrs
      let newVoters = new Set(oldAttrs?.voters ?? [])
      if (votedByMe) {
        newVoters.delete(auth.id)
      } else {
        newVoters.add(auth.id)
      }
      dispatch(tr.setNodeMarkup(getPos(), undefined, { ...oldAttrs, voters: Array.from(newVoters) }))
    }

    const getVotersString = () => {
      const votersArray = Array.from(voters)
      if (votersArray.length === 0) {
        return 'No votes'
      } else {
        const voterString: string[] = []
        const votedByMe = votersArray.includes(auth?.id ?? NaN)
        const votersWithoutYou = votersArray.filter((voter) => voter !== auth?.id)
        const lastVoter = last(votersWithoutYou)
        const votersWithoutLast = votersArray.filter((voterId) => voterId !== lastVoter)
        const votersWithoutYouOrLast = votersWithoutYou.filter((voterId) => voterId !== lastVoter)

        if (votedByMe) {
          voterString.push('You')
        }
        if (votedByMe && votersWithoutYouOrLast.length > 0) {
          voterString.push(', ')
        }
        if (votersWithoutYouOrLast.length > 0) {
          voterString.push(`${votersWithoutYouOrLast.map((id) => userNames.get(id)).join(', ')}`)
        }
        if (lastVoter) {
          if (votersWithoutLast.length > 0) {
            voterString.push(' and ')
          }
          voterString.push(userNames.get(lastVoter) ?? '')
        }
        return `${voterString.join('')} voted`
      }
    }

    return (
      <div className={cx(styles.VoteListItem, { [styles.votingDisabled]: !isVotable })}>
        <Tooltip label={getVotersString()}>
          <button
            className={cx(styles.voteWidget, { [styles.votedByMe]: votedByMe })}
            onClick={handleVote}
            contentEditable={false}
          >
            <ArrowUpIcon />
            <span className={styles.voteCount}>{voters.size}</span>
          </button>
        </Tooltip>
        <div className={styles.content} ref={contentDomRef} />
      </div>
    )
  }),
)
